(() => {
  if (!window.customElements) return;
  if (customElements.get('wr-trial-booking')) return;

  class WrTrialBooking extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleLocationChange = this.handleLocationChange.bind(this);
      this.handleBookingInput = this.handleBookingInput.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleRefresh = this.handleRefresh.bind(this);
      this.handleSectionUnload = this.handleSectionUnload.bind(this);
      this.finlandDateFormatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Helsinki',
        calendar: 'gregory',
        numberingSystem: 'latn',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      });
    }

    connectedCallback() {
      if (this.isInitialized) return;

      this.formRegion = this.querySelector('[data-booking-form]');
      this.locationSelect = this.querySelector('[data-location-select]');
      this.locationStatus = this.querySelector('[data-location-status]');
      this.locationButtons = Array.from(this.querySelectorAll('[data-location-trigger]'));
      this.bookingDate = this.querySelector('[data-booking-date]');
      this.bookingTime = this.querySelector('[data-booking-time]');
      this.bookingStatus = this.querySelector('[data-booking-status]');
      this.initialBookingDate = this.bookingDate?.dataset.initialValue || '';
      this.initialBookingTime = this.bookingTime?.dataset.initialValue || '';
      this.bookingForm = this.querySelector('form');
      this.minimumNoticeHours = Math.max(1, Number.parseInt(this.dataset.minimumNoticeHours, 10) || 24);
      this.bookingWindowDays = Math.max(1, Number.parseInt(this.dataset.bookingWindowDays, 10) || 30);
      this.bookingStartTime = this.dataset.bookingStartTime || '08:00';
      this.bookingEndTime = this.dataset.bookingEndTime || '20:00';

      if (!this.formRegion || !this.locationSelect) return;

      this.isInitialized = true;
      this.addEventListener('click', this.handleClick);
      this.locationSelect.addEventListener('change', this.handleLocationChange);
      this.bookingDate?.addEventListener('input', this.handleBookingInput);
      this.bookingDate?.addEventListener('change', this.handleBookingInput);
      this.bookingTime?.addEventListener('input', this.handleBookingInput);
      this.bookingTime?.addEventListener('change', this.handleBookingInput);
      this.bookingForm?.addEventListener('submit', this.handleSubmit);
      window.addEventListener('focus', this.handleRefresh);
      window.addEventListener('pageshow', this.handleRefresh);
      document.addEventListener('visibilitychange', this.handleRefresh);
      this.editorSection = this.closest('.shopify-section');
      this.editorSection?.addEventListener('shopify:section:unload', this.handleSectionUnload);

      const shouldStartClosed =
        this.dataset.formState === 'after_selection' && this.formRegion.dataset.formResult === 'idle';

      if (shouldStartClosed) {
        this.formRegion.hidden = true;
      }

      this.dataset.enhanced = 'true';
      this.updateExpandedState();
      this.updateActiveLocation(this.locationSelect.value, false);
      this.updateBookingConstraints();
    }

    disconnectedCallback() {
      if (!this.isInitialized) return;

      this.removeEventListener('click', this.handleClick);
      this.locationSelect?.removeEventListener('change', this.handleLocationChange);
      this.bookingDate?.removeEventListener('input', this.handleBookingInput);
      this.bookingDate?.removeEventListener('change', this.handleBookingInput);
      this.bookingTime?.removeEventListener('input', this.handleBookingInput);
      this.bookingTime?.removeEventListener('change', this.handleBookingInput);
      this.bookingForm?.removeEventListener('submit', this.handleSubmit);
      window.removeEventListener('focus', this.handleRefresh);
      window.removeEventListener('pageshow', this.handleRefresh);
      document.removeEventListener('visibilitychange', this.handleRefresh);
      this.editorSection?.removeEventListener('shopify:section:unload', this.handleSectionUnload);
      window.clearTimeout(this.refreshTimer);
      window.cancelAnimationFrame(this.focusFrame);
      this.refreshTimer = null;
      this.focusFrame = null;
      this.isInitialized = false;
    }

    handleSectionUnload() {
      this.disconnectedCallback();
    }

    handleRefresh() {
      if (!this.isInitialized || document.visibilityState === 'hidden') return;
      this.updateBookingConstraints();
    }

    handleClick(event) {
      const trigger = event.target.closest('[data-location-trigger]');
      if (!trigger || trigger.closest('wr-trial-booking') !== this) return;

      const locationValue = trigger.dataset.locationValue || '';
      if (!locationValue) return;

      this.locationSelect.value = locationValue;
      this.locationSelect.dispatchEvent(new Event('change', { bubbles: true }));
      this.openForm();
    }

    handleLocationChange() {
      this.updateActiveLocation(this.locationSelect.value, true);
    }

    handleBookingInput() {
      this.updateBookingConstraints();
      this.validateDesiredDateTime();
    }

    handleSubmit(event) {
      this.updateBookingConstraints();

      if (this.validateDesiredDateTime()) return;

      event.preventDefault();
      const invalidField = [this.bookingDate, this.bookingTime].find((field) => field && !field.validity.valid);
      invalidField?.reportValidity();
    }

    getMinimumDesiredDateTime(now = Date.now()) {
      // Notice is elapsed time, not a calendar day: DST days can be 23 or 25 hours.
      return new Date(now + this.minimumNoticeHours * 60 * 60 * 1000);
    }

    getMaximumDesiredDate(now = Date.now()) {
      return this.addCalendarDays(this.formatLocalDate(new Date(now)), this.bookingWindowDays);
    }

    timeToMinutes(timeValue) {
      const [hours, minutes] = String(timeValue).split(':').map(Number);
      return hours * 60 + minutes;
    }

    formatLocalDate(date) {
      const parts = this.getFinlandParts(date);
      return `${parts.year}-${parts.month}-${parts.day}`;
    }

    getFinlandParts(date) {
      return Object.fromEntries(
        this.finlandDateFormatter.formatToParts(date)
          .filter((part) => part.type !== 'literal')
          .map((part) => [part.type, part.value])
      );
    }

    addCalendarDays(dateValue, days) {
      // UTC is only a neutral carrier for calendar arithmetic, never the booking zone.
      const date = new Date(`${dateValue}T12:00:00Z`);
      date.setUTCDate(date.getUTCDate() + days);
      return date.toISOString().slice(0, 10);
    }

    getFinlandDateTime(dateValue, timeValue) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateValue) || !/^\d{2}:\d{2}$/.test(timeValue)) return null;

      const wallTime = Date.parse(`${dateValue}T${timeValue}:00Z`);
      if (!Number.isFinite(wallTime)) return null;
      let instant = wallTime;

      // Resolve Helsinki's offset for the target date, including summer/winter time.
      // Booking hours (08–20) and midnight do not fall in the DST overlap/gap.
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const parts = this.getFinlandParts(new Date(instant));
        const representedTime = Date.parse(
          `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}Z`
        );
        const adjustment = wallTime - representedTime;
        instant += adjustment;
        if (!adjustment) break;
      }

      const result = new Date(instant);
      const parts = this.getFinlandParts(result);
      return this.formatLocalDate(result) === dateValue && `${parts.hour}:${parts.minute}` === timeValue
        ? result
        : null;
    }

    formatDateLabel(date) {
      const formatter = new Intl.DateTimeFormat(document.documentElement.lang || 'fi-FI', {
        timeZone: 'Europe/Helsinki',
        weekday: 'short',
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
      });
      const label = formatter.format(date);
      return label.charAt(0).toUpperCase() + label.slice(1);
    }

    replaceSelectOptions(select, optionDefinitions, preferredValue) {
      if (!select) return;

      const placeholder = Array.from(select.options).find((option) => !option.value);

      // A focus/visibility refresh must not disturb an open native select unnecessarily.
      const currentOptions = Array.from(select.options).filter((option) => option.value);
      if (currentOptions.length === optionDefinitions.length && currentOptions.every((option, index) =>
        option.value === optionDefinitions[index].value && option.textContent === optionDefinitions[index].label
      )) {
        select.value = optionDefinitions.some((definition) => definition.value === preferredValue) ? preferredValue : '';
        return;
      }

      select.replaceChildren();
      if (placeholder) select.append(placeholder);

      optionDefinitions.forEach((definition) => {
        const option = document.createElement('option');
        option.value = definition.value;
        option.textContent = definition.label;
        select.append(option);
      });

      select.value = optionDefinitions.some((definition) => definition.value === preferredValue)
        ? preferredValue
        : '';
    }

    getDateOptionDefinitions(minimumDateTime, maximumDate) {
      const optionDefinitions = [];
      let date = this.formatLocalDate(minimumDateTime);

      while (date <= maximumDate) {
        const lastSlot = this.getFinlandDateTime(date, this.bookingEndTime);
        if (lastSlot && lastSlot >= minimumDateTime) {
          optionDefinitions.push({ value: date, label: this.formatDateLabel(lastSlot) });
        }
        date = this.addCalendarDays(date, 1);
      }

      return optionDefinitions;
    }

    getTimeOptionDefinitions(minimumDateTime, dateValue = this.bookingDate?.value) {
      if (!dateValue) return [];

      const startMinutes = this.timeToMinutes(this.bookingStartTime);
      const endMinutes = this.timeToMinutes(this.bookingEndTime);
      const optionDefinitions = [];

      for (let minutes = Math.ceil(startMinutes / 60) * 60; minutes <= endMinutes; minutes += 60) {
        const hours = String(Math.floor(minutes / 60)).padStart(2, '0');
        const value = `${hours}:00`;
        const slot = this.getFinlandDateTime(dateValue, value);
        if (slot && slot >= minimumDateTime) optionDefinitions.push({ value, label: value });
      }

      return optionDefinitions;
    }

    updateBookingConstraints() {
      if (!this.bookingDate || !this.bookingTime) return;

      const now = Date.now();
      const minimumDateTime = this.getMinimumDesiredDateTime(now);
      const maximumDate = this.getMaximumDesiredDate(now);
      const preferredDate = this.bookingDate.value || this.initialBookingDate;
      const preferredTime = this.bookingTime.value || this.initialBookingTime;
      const dateOptions = this.getDateOptionDefinitions(minimumDateTime, maximumDate);

      this.replaceSelectOptions(
        this.bookingDate,
        dateOptions,
        preferredDate
      );
      this.replaceSelectOptions(
        this.bookingTime,
        this.getTimeOptionDefinitions(minimumDateTime),
        preferredTime
      );

      this.bookingTime.disabled = !this.bookingDate.value;
      this.initialBookingDate = '';
      this.initialBookingTime = '';

      if (preferredDate || preferredTime) {
        const dateExpired = preferredDate && !this.bookingDate.value;
        const timeExpired = preferredTime && !this.bookingTime.value;
        const valid = this.validateDesiredDateTime();
        if (this.bookingStatus && (dateExpired || timeExpired)) {
          this.bookingStatus.textContent = dateExpired
            ? 'Aiemmin valitsemasi päivä ei ole enää valittavissa. Valitse uusi päivä ja kellonaika Suomen ajassa.'
            : 'Aiemmin valitsemasi kellonaika ei ole enää valittavissa. Valitse uusi kellonaika Suomen ajassa.';
        } else if (this.bookingStatus && valid) {
          this.bookingStatus.textContent = '';
        }
      }

      this.scheduleRefresh(now, minimumDateTime, dateOptions);
    }

    scheduleRefresh(now, minimumDateTime, dateOptions) {
      window.clearTimeout(this.refreshTimer);
      if (!this.isInitialized) return;

      const tomorrow = this.addCalendarDays(this.formatLocalDate(new Date(now)), 1);
      let nextRefresh = this.getFinlandDateTime(tomorrow, '00:00').getTime();
      const firstDate = dateOptions[0]?.value;
      const firstTime = firstDate && this.getTimeOptionDefinitions(minimumDateTime, firstDate)[0]?.value;
      if (firstTime) {
        const slot = this.getFinlandDateTime(firstDate, firstTime).getTime();
        // A slot exactly 24 h away is valid; one millisecond later it has expired.
        nextRefresh = Math.min(nextRefresh, slot - this.minimumNoticeHours * 60 * 60 * 1000 + 1);
      }
      this.refreshTimer = window.setTimeout(this.handleRefresh, Math.max(1, nextRefresh - Date.now()));
    }

    parseDesiredDateTime() {
      if (!this.bookingDate?.value || !this.bookingTime?.value) return null;

      return this.getFinlandDateTime(this.bookingDate.value, this.bookingTime.value);
    }

    validateDesiredDateTime() {
      if (!this.bookingDate || !this.bookingTime) return true;

      this.bookingDate.setCustomValidity('');
      this.bookingTime.setCustomValidity('');

      if (!this.bookingDate.value || !this.bookingTime.value) {
        const hasPartialSelection = Boolean(this.bookingDate.value || this.bookingTime.value);
        if (!this.bookingDate.required && !hasPartialSelection) return true;

        if (!this.bookingDate.value) {
          this.bookingDate.setCustomValidity('Valitse päivä.');
        } else {
          this.bookingTime.setCustomValidity('Valitse kellonaika.');
        }

        return false;
      }

      const desiredDateTime = this.parseDesiredDateTime();
      if (!desiredDateTime) {
        this.bookingDate.setCustomValidity('Valitse kelvollinen päivä ja kellonaika Suomen ajassa.');
        return false;
      }

      const now = Date.now();
      const maximumDate = this.getMaximumDesiredDate(now);
      const minimumDateTime = this.getMinimumDesiredDateTime(now);
      const minimumDate = this.formatLocalDate(minimumDateTime);

      if (this.bookingDate.value > maximumDate) {
        this.bookingDate.setCustomValidity(`Valitse päivä seuraavien ${this.bookingWindowDays} päivän ajalta.`);
        return false;
      }

      if (this.bookingDate.value < minimumDate) {
        this.bookingDate.setCustomValidity(`Valitse päivä aikaisintaan ${this.minimumNoticeHours} tunnin päähän.`);
        return false;
      }

      const desiredMinutes = this.timeToMinutes(this.bookingTime.value);
      const startMinutes = this.timeToMinutes(this.bookingStartTime);
      const endMinutes = this.timeToMinutes(this.bookingEndTime);

      if (desiredMinutes % 60 !== 0 || desiredMinutes < startMinutes || desiredMinutes > endMinutes) {
        this.bookingTime.setCustomValidity('Valitse tasatunti klo 8.00–20.00 väliltä.');
        return false;
      }

      if (desiredDateTime >= minimumDateTime) return true;

      this.bookingTime.setCustomValidity(
        `Valitse tasatunti vähintään ${this.minimumNoticeHours} tunnin päähän klo 8.00–20.00 väliltä.`
      );
      return false;
    }

    updateActiveLocation(locationValue, announceSelection) {
      this.locationButtons.forEach((button) => {
        const isActive = Boolean(locationValue) && button.dataset.locationValue === locationValue;
        button.setAttribute('aria-pressed', String(isActive));
        button.closest('[data-location-card]')?.classList.toggle('is-active', isActive);
      });

      if (announceSelection && this.locationStatus) {
        this.locationStatus.textContent = locationValue
          ? `${this.dataset.selectedLabel || 'Valittu kokeilupaikka'}: ${locationValue}`
          : '';
      }
    }

    updateExpandedState() {
      const isOpen = !this.formRegion.hidden;
      this.locationButtons.forEach((button) => button.setAttribute('aria-expanded', String(isOpen)));
    }

    openForm() {
      this.updateBookingConstraints();
      this.formRegion.hidden = false;
      this.updateExpandedState();

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.formRegion.scrollIntoView({
        behavior: reducedMotion ? 'auto' : 'smooth',
        block: 'start',
      });

      const focusTarget =
        this.formRegion.querySelector('[data-booking-form-heading]') ||
        this.formRegion.querySelector('input:not([type="hidden"]), select, textarea, button');

      if (!focusTarget) return;

      window.cancelAnimationFrame(this.focusFrame);
      this.focusFrame = window.requestAnimationFrame(() => {
        this.focusFrame = null;
        if (!this.isInitialized) return;
        try {
          focusTarget.focus({ preventScroll: true });
        } catch (error) {
          focusTarget.focus();
        }
      });
    }
  }

  customElements.define('wr-trial-booking', WrTrialBooking);
})();
